// src/pages/Analytics.js
import React, { useState, useEffect, useMemo } from 'react';
import api from '../../api/axiosInstance';
import { Link, useOutletContext } from 'react-router-dom';
import { FiEye, FiClock, FiActivity, FiFileText } from 'react-icons/fi';
import { Bar, Doughnut } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, ArcElement, Title, Tooltip, Legend } from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Title, Tooltip, Legend);

/**
 * Parses a user agent string to identify the browser or client, grouping all versions under a single name.
 * This version is case-insensitive and designed to prevent duplicate browser entries.
 * @param {string} uaString The full user agent string.
 * @returns {string} A simplified name for the client (e.g., "Chrome", "Safari", "Google Bot").
 */
const parseUserAgent = (uaString) => {
    if (!uaString) return "Unknown";

    const ua = uaString.toLowerCase(); // Use a lowercased version for case-insensitive matching

    // --- Common Bots and Proxies ---
    if (ua.includes('googleimageproxy')) return 'Google Image Proxy';
    if (ua.includes('yahoomailproxy')) return 'Yahoo Mail Proxy';
    if (ua.includes('bingbot')) return 'Bing Bot';
    if (ua.includes('googlebot')) return 'Google Bot';
    if (ua.includes('ms-office')) return 'Microsoft Office';
    if (ua.includes('go-http-client')) return 'Go HTTP Client';

    // --- Browsers (Order is important to avoid misidentification) ---
    if (ua.includes('opr/')) return 'Opera';
    if (ua.includes('edg/')) return 'Edge';
    if (ua.includes('edge/')) return 'Edge (Legacy)';
    if (ua.includes('firefox/')) return 'Firefox';
    if (ua.includes('safari/') && !ua.includes('chrome') && !ua.includes('edg')) return 'Safari';
    if (ua.includes('chrome/')) return 'Chrome';

    // --- Fallback for anything not identified ---
    return "Other";
};

const Analytics = () => {
    const outletContext = useOutletContext();
    const [setBreadcrumb, setBreadcrumbLink] = outletContext || [null, null];
    const [stats, setStats] = useState(null);
    const [monthlyCount, setMonthlyCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [lastUpdated, setLastUpdated] = useState(null);

    useEffect(() => {
        if (setBreadcrumb && setBreadcrumbLink) {
            setBreadcrumb(['Countdown', 'Analytics']);
            setBreadcrumbLink(['/countdown']);
        }
        fetchAnalytics();
    }, [setBreadcrumb, setBreadcrumbLink]);

    const fetchAnalytics = async () => {
        setLoading(true);
        try {
            const [analyticsRes, monthlyCountRes] = await Promise.all([
                api.get('/logs/analytics'),
                api.get('/logs/count/monthly')
            ]);
            setStats(analyticsRes.data);
            setMonthlyCount(monthlyCountRes.data.totalRequestsThisMonth);
            let LastUpdated = new Date();
            LastUpdated.setDate(LastUpdated.getDate() - 1);
            LastUpdated.setHours(0, 0, 0, 0);
            setLastUpdated(LastUpdated); // Set the last updated time
        } catch (err) {
            console.error("Failed to fetch analytics:", err);
        } finally {
            setLoading(false);
        }
    };

    // --- Memoized Data Processing and Aggregation ---
    const processedStats = useMemo(() => {
        if (!stats) return null;

        // --- Aggregate User Agents ---
        const agentCounts = {};
        (stats.userAgents || []).forEach(item => {
            const agentName = parseUserAgent(item.userAgent);
            agentCounts[agentName] = (agentCounts[agentName] || 0) + item.count;
        });

        // Sort aggregated agents by count
        const sortedAgents = Object.entries(agentCounts)
            .sort(([, countA], [, countB]) => countB - countA);

        const userAgentChartData = {
            labels: sortedAgents.map(([name]) => name).slice(0, 10), // Top 10
            datasets: [{
                label: 'Requests',
                data: sortedAgents.map(([, count]) => count).slice(0, 10),
                backgroundColor: 'rgba(57, 89, 255, 0.8)',
            }]
        };

        // --- Process Status Codes ---
        const totalRequests = (stats.statusCodes || []).reduce((sum, current) => sum + current.count, 0);
        const successfulRequests = (stats.statusCodes || [])
            .filter(item => item.statusCode < 400)
            .reduce((sum, current) => sum + current.count, 0);
        const uptimePercentage = totalRequests > 0 ? (successfulRequests / totalRequests) * 100 : 100;

        const statusChartData = {
            labels: (stats.statusCodes || []).map(item => `Status ${item.statusCode}`),
            datasets: [{
                data: (stats.statusCodes || []).map(item => item.count),
                backgroundColor: ['#4ade80', '#facc15', '#f87171', '#9ca3af'],
                borderColor: '#ffffff',
                borderWidth: 2,
            }],
        };

        return {
            averageLatency: parseFloat(stats.averageLatency) || 0,
            uptimePercentage,
            userAgentChartData,
            statusChartData
        };
    }, [stats]);

    if (loading) return (<div className="flex items-center justify-center w-full h-[90vh]">
        <div role="status">
            <svg aria-hidden="true" className="w-8 h-8 text-gray-200 animate-spin dark:text-gray-600 fill-blue-600" viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="currentColor" /><path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentFill" /></svg>
            <span className="sr-only">Loading...</span>
        </div>
    </div>);

    return (
        <div className="p-6 bg-brand-background min-h-screen font-sans">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
                <div>
                    <h1 className="text-3xl font-bold text-brand-heading">Performance Analytics</h1>
                    {lastUpdated && (
                        <p className="text-sm text-gray-500 mt-1">
                            Last updated: {lastUpdated.toLocaleString()}
                        </p>
                    )}
                </div>
                <Link to="/countdown/analytics/logs" className="flex items-center gap-2 py-2 px-4 text-sm font-medium text-brand-accent bg-white border border-brand-border rounded-lg hover:bg-gray-50 transition-colors">
                    <FiFileText />
                    View Raw Logs
                </Link>
            </div>


            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                <AnalyticsCard
                    icon={<FiEye className="text-brand-accent" />}
                    title="Views This Month"
                    value={(monthlyCount || 0).toLocaleString()}
                    desc="Current billing cycle"
                />
                <AnalyticsCard
                    icon={<FiClock className="text-yellow-500" />}
                    title="Avg. Latency"
                    value={`${processedStats?.averageLatency.toFixed(2) || '0.00'} ms`}
                    desc="Server response time"
                />
                <AnalyticsCard
                    icon={<FiActivity className="text-brand-success" />}
                    title="Uptime"
                    value={`${processedStats?.uptimePercentage.toFixed(2) || '100.00'}%`}
                    desc="Based on successful requests"
                />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
                <div className="bg-white p-5 rounded-lg border border-brand-border lg:col-span-3">
                    <h3 className="text-lg font-bold text-brand-heading mb-4">Top User Agents</h3>
                    <div className="h-80">
                        {processedStats && <Bar data={processedStats.userAgentChartData} options={{ maintainAspectRatio: false, indexAxis: 'y', plugins: { legend: { display: false } } }} />}
                    </div>
                </div>
                <div className="bg-white p-5 rounded-lg border border-brand-border lg:col-span-2">
                    <h3 className="text-lg font-bold text-brand-heading mb-4">Response Status Codes</h3>
                    <div className="h-80 flex items-center justify-center">
                        {processedStats && <Doughnut data={processedStats.statusChartData} options={{ maintainAspectRatio: false, plugins: { legend: { position: 'right' } } }} />}
                    </div>
                </div>
            </div>
        </div>
    );
};

// Child Component for main stat cards
const AnalyticsCard = ({ icon, title, value, desc }) => (
    <div className="bg-white p-5 rounded-lg border border-brand-border">
        <div className="flex items-center gap-4">
            <div className="p-3 bg-gray-100 rounded-full">{icon}</div>
            <div>
                <p className="text-sm text-brand-paragraph">{title}</p>
                <p className="text-2xl font-bold text-brand-heading">{value}</p>
                <p className="text-xs text-gray-500">{desc}</p>
            </div>
        </div>
    </div>
);

export default Analytics;
